import { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PAYMENT_METHODS } from '../../../services/exports/Constants';
import LoadingIndicator from '../../loading/LoadingIndicator';
import paymentLog from '../../../services/helpers/PaymentLogger';
import loadBraintreeScripts from '../../../services/helpers/loadBraintreeScripts';
import useDialogFocus from '../../../hooks/ui/useDialogFocus';
import type { SpreedlyBraintreeSheetState } from '../../../hooks/payment/useSpreedlyOffsitePayment';
import VenmoWordmark from '../../../assets/icons/payment-methods/VenmoWordmark';

export const SPREEDLY_BRAINTREE_ELEMENT_EL_ID = 'spreedly-braintree-wallet-button';

/**
 * Spreedly reports most setup failures (Braintree client, wallet create) only to the console, so a
 * button that never turns ready is the one signal there is.
 */
export const BRAINTREE_BUTTON_TIMEOUT_MS = 15_000;

/**
 * How long a pressed button may go without the wallet reporting anything. A popup the browser
 * blocked, or a wrapper that sent it to another app, never calls back — the customer would sit on
 * "approve the payment in the window" with only Cancel. Generous, because a real approval means
 * logging in to the wallet.
 */
export const BRAINTREE_APPROVAL_TIMEOUT_MS = 120_000;

interface Props {
  sheet: SpreedlyBraintreeSheetState;
  onResult: (result: Spreedly.BraintreeCallbackResult) => Promise<void>;
  onCancel: () => void;
}

/**
 * The Braintree PayPal or Venmo button for a `processing` purchase, rendered by Spreedly's iFrame.
 * The wallet only opens from a click on its own button, so the customer presses it here; what it
 * reports back is confirmed with the backend, which settles the order there and then.
 *
 * Follows Braintree's button rules: nothing is preselected, the button carries the wallet's own
 * artwork, and a button that is not ready yet is not shown.
 *
 * The two wallets arrive differently. PayPal draws its own button and reports `Initiated`. For
 * Venmo, Spreedly's bundle draws nothing and reports nothing: once Braintree's Venmo client
 * exists it sets `display: block` on our element and listens for clicks on it. So the Venmo button
 * is ours, and it is ready when the bundle shows the element.
 */
export default function SpreedlyBraintreeSheet(props: Props) {
  const { sheet, onResult, onCancel } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Components:Payment:Spreedly:SpreedlyBraintreeSheet' });
  const [ready, setReady] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const isVenmo = sheet.method === 'venmo';

  useDialogFocus(panelRef);

  const methodName =
    PAYMENT_METHODS().find((method) => method.payment_method_id === sheet.method)?.display_name ?? sheet.method;

  useEffect(() => {
    let cancelled = false;

    const unavailable = (message: string) => {
      paymentLog.error('spreedly', 'offsite_wallet_buttons_failed', { payment_method: sheet.method, message });

      if (!cancelled) {
        setError(t('unavailable'));
      }
    };

    loadBraintreeScripts(sheet.method)
      .then(() => {
        if (cancelled) {
          return;
        }

        const created = window.Spreedly?.createBraintreePaymentElements?.({
          transactionToken: sheet.transactionToken,
          environmentKey: sheet.environmentKey,
          paymentElements: { [sheet.method]: SPREEDLY_BRAINTREE_ELEMENT_EL_ID },
          callbackActionButtonFunction: (event) => {
            if (cancelled) {
              return;
            }

            if (event.state === 'Initiated') {
              setReady(true);
            } else if (event.state === 'Clicked') {
              setClicked(true);
            }
          },
          callbackFunction: (result) => {
            if (cancelled) {
              return;
            }

            setClicked(false);

            if (result.state === 'Successful') {
              setConfirming(true);
            }

            onResult(result);
          },
        });

        if (!window.Spreedly?.createBraintreePaymentElements) {
          unavailable('sdk_missing');
        } else if (created?.error) {
          unavailable(created.error);
        }
      })
      .catch((loadError: Error) => unavailable(loadError?.message ?? 'script_load_failed'));

    return () => {
      cancelled = true;
    };
  }, [sheet.transactionToken]);

  useEffect(() => {
    const element = elementRef.current;

    if (!isVenmo || !element) {
      return;
    }

    const observer = new MutationObserver(() => element.style.display === 'block' && setReady(true));
    observer.observe(element, { attributes: true, attributeFilter: ['style'] });

    return () => observer.disconnect();
  }, [isVenmo, sheet.transactionToken]);

  useEffect(() => {
    if (ready || error) {
      return;
    }

    const timer = setTimeout(() => {
      paymentLog.error('spreedly', 'offsite_wallet_buttons_timeout', { payment_method: sheet.method });
      setError(t('unavailable'));
    }, BRAINTREE_BUTTON_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [ready, error, sheet.transactionToken]);

  // Pressed, and then nothing: the wallet's window never reported back.
  useEffect(() => {
    if (!clicked || confirming || error) {
      return;
    }

    const timer = setTimeout(() => {
      paymentLog.warn('spreedly', 'offsite_wallet_approval_timeout', { payment_method: sheet.method });
      setClicked(false);
      setError(t('approval_timeout', { method: methodName }));
    }, BRAINTREE_APPROVAL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [clicked, confirming, error, sheet.transactionToken]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !confirming) {
        onCancel();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, confirming]);

  return (
    <div className={overlay} data-test="spreedly-wallet-sheet">
      <div
        ref={panelRef}
        tabIndex={-1}
        className={panel}
        role="dialog"
        aria-modal="true"
        aria-label={t('title', { method: methodName })}
      >
        <p className={title}>{t('title', { method: methodName })}</p>
        {!error && !confirming && <p className={hint}>{t('hint', { method: methodName })}</p>}
        {(!ready || confirming) && !error && (
          <div className="flex justify-center py-md">
            <LoadingIndicator />
          </div>
        )}
        {/* Laid out from the start — PayPal sizes its button to the container it renders into — and
            only faded in once the wallet is ready, so no half-built button shows. Hidden from the
            wrapper: the bundle sets an inline display on the element itself. */}
        <div className={classnames({ hidden: confirming || !!error })}>
          <div
            ref={elementRef}
            id={SPREEDLY_BRAINTREE_ELEMENT_EL_ID}
            className={classnames('min-h-[48px]', { 'opacity-0 pointer-events-none': !ready })}
            data-test="spreedly-wallet-button"
          >
            {isVenmo && (
              <button
                type="button"
                className={venmoButton}
                aria-label="Venmo"
                onClick={() => setClicked(true)}
                data-test="spreedly-venmo-button"
              >
                <VenmoWordmark className="h-[18px] text-white" />
              </button>
            )}
          </div>
        </div>
        {confirming && (
          <p className={hint} role="status" data-test="spreedly-wallet-confirming">
            {t('confirming')}
          </p>
        )}
        {error && (
          <p className={errorText} role="alert" data-test="spreedly-wallet-sheet-error">
            {error}
          </p>
        )}
        <button
          type="button"
          className={cancelButton}
          onClick={onCancel}
          disabled={confirming}
          data-test="spreedly-wallet-cancel"
        >
          {t('cancel')}
        </button>
      </div>
    </div>
  );
}

const overlay = 'fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-md';
const panel = 'bg-brand-background-primary rounded-lg p-md w-full max-w-[480px]';
const title = 'text-md font-semibold mb-xs text-brand-text-primary dark:text-white';
const hint = 'text-sm mb-sm text-brand-text-primary opacity-80 dark:text-white';
const errorText = 'mt-sm text-sm text-danger';
const venmoButton = 'w-full h-[48px] rounded-md bg-[#008CFF] flex items-center justify-center hover:opacity-90';
const cancelButton =
  'mt-sm block w-full text-center text-sm font-medium underline underline-offset-2 text-brand-text-primary hover:opacity-80 disabled:opacity-50 dark:text-white';
